import { supabase } from '../lib/supabase';
import { SocialPost, CreateSocialPostDTO, UpdateSocialPostDTO } from '../types/social';

export class SocialService {
    private table = 'social_posts';

    /**
     * Obtiene todos los posts activos
     */
    async getActivePosts(): Promise<SocialPost[]> {
        const { data, error } = await supabase
            .from(this.table)
            .select('*')
            .eq('is_active', true)
            .order('created_at', { ascending: false });

        if (error) throw error;
        return data || [];
    }

    /**
     * Obtiene los posts destacados para el carrusel
     */
    async getFeaturedPosts(): Promise<SocialPost[]> {
        const { data, error } = await supabase
            .from(this.table)
            .select('*')
            .eq('is_active', true)
            .eq('is_featured', true)
            .order('created_at', { ascending: false });

        if (error) throw error;
        return data || [];
    }

    /**
     * Obtiene todos los posts (para el panel admin)
     */
    async getAllPosts(): Promise<SocialPost[]> {
        const { data, error } = await supabase
            .from(this.table)
            .select('*')
            .order('created_at', { ascending: false });

        if (error) throw error;
        return data || [];
    }

    /**
     * Crea un nuevo post
     */
    async createPost(post: CreateSocialPostDTO): Promise<SocialPost> {
        const { data, error } = await supabase
            .from(this.table)
            .insert([post])
            .select()
            .single();

        if (error) throw error;
        return data;
    }

    /**
     * Actualiza un post
     */
    async updatePost(id: string, updates: UpdateSocialPostDTO): Promise<SocialPost> {
        const { data, error } = await supabase
            .from(this.table)
            .update(updates)
            .eq('id', id)
            .select()
            .single();

        if (error) throw error;
        return data;
    }

    /**
     * Elimina un post
     */
    async deletePost(id: string): Promise<void> {
        const { error } = await supabase
            .from(this.table)
            .delete()
            .eq('id', id);

        if (error) throw error;
    }

    /**
     * Extrae un video de TikTok y lo sube al bucket usando una Edge Function
     */
    async extractTikTokVideo(tiktokUrl: string): Promise<{ url: string, caption: string }> {
        const { data, error } = await supabase.functions.invoke('tiktok-extractor', {
            body: { url: tiktokUrl }
        });

        if (error) throw error;
        return data;
    }

    /**
     * Sube un video al bucket de storage
     */
    async uploadVideo(file: File): Promise<string> {
        const fileName = `${Date.now()}-${file.name}`;
        const { data, error } = await supabase.storage
            .from('social-videos')
            .upload(fileName, file);

        if (error) throw error;

        const { data: { publicUrl } } = supabase.storage
            .from('social-videos')
            .getPublicUrl(data.path);

        return publicUrl;
    }
}

export const socialService = new SocialService();
